import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Frame,
  Navigation,
  Page,
  Layout,
  LegacyCard as Card,
  Text,
  Button,
  Banner,
  Spinner,
  Toast,
  InlineStack,
  BlockStack,
  Box,
  TextField,
} from "@shopify/polaris";
import type { ShopifyGlobal } from "@shopify/app-bridge-types";
import {
  createApiClient,
  ConnectionStatus,
  type CatalogCreatePayload,
} from "../lib/api";
import { ProductBrowser } from "../components/ProductBrowser";

interface BookListResponse {
  data: {
    books?: Book[];
    data?: Book[];
  };
}

interface Book {
  random_id: string;
  name: string;
  status?: string;
  created_at?: string;
  preview_url: string;
  workspace_slug?: string;
  embed_code?: string;
  page_count?: number;
}

type CopyKind = "id" | "url" | "code";

// Polaris treats a duration of 0 as its default five seconds. Use the largest
// browser-safe timeout so rendering progress does not disappear on its own;
// terminal render states replace it with a normal-duration result toast.
const PERSISTENT_TOAST_DURATION = 2_147_483_647;

// Liquid filename of the app block (packages/theme-extension/blocks/flipbook-embed.liquid).
// The theme-editor deep link addresses it as {api_key}/{handle}.
const THEME_BLOCK_HANDLE = "flipbook-embed";

function getShopifyGlobal(): ShopifyGlobal | undefined {
  return (window as typeof window & { shopify?: ShopifyGlobal }).shopify;
}

// Merchant-facing Book IDs use the same stable public identifier as the
// reader URL. Never fall back to the internal auto-incrementing database id.
function getPublicBookId(book: Book): string {
  return book.random_id || "";
}

function getWorkspaceSlug(book: Book): string {
  if (book.workspace_slug) return book.workspace_slug;

  try {
    const segments = new URL(book.preview_url).pathname
      .split("/")
      .filter(Boolean);
    const booksIndex = segments.indexOf("books");
    const encodedSlug = booksIndex >= 0 ? segments[booksIndex + 1] : undefined;
    return encodedSlug ? decodeURIComponent(encodedSlug) : "";
  } catch {
    return "";
  }
}

// Full storefront embed URL, matching the platform's public embed route:
// {embedBaseUrl}/#/embed/{workspace_slug}/{random_id}
function getEmbedUrl(book: Book, embedBaseUrl: string | undefined): string {
  const randomId = getPublicBookId(book);
  const workspaceSlug = getWorkspaceSlug(book);
  if (!embedBaseUrl || !workspaceSlug || !randomId) return "";
  return `${embedBaseUrl.replace(/\/+$/, "")}/#/embed/${encodeURIComponent(workspaceSlug)}/${encodeURIComponent(randomId)}`;
}

// Generic iframe snippet a merchant can paste anywhere (blogs, custom themes, etc.).
function getEmbedCode(book: Book, embedBaseUrl: string | undefined): string {
  const src = getEmbedUrl(book, embedBaseUrl);
  if (!src) return "";
  return `<iframe src="${src}" style="width:100%;height:600px;border:none;" loading="lazy" allow="fullscreen"></iframe>`;
}

// Books that aren't fully processed/published yet shouldn't be viewable or embedded.
function isBookReady(book: Book): boolean {
  return book.status !== "draft" && book.status !== "processing";
}

// App-block deep link. Per Shopify, addAppBlockId is {api_key}/{handle} and
// context=apps must NOT be set (that's only for app embed blocks).
function getThemeEditorUrl(
  shopDomain: string | undefined,
  apiKey: string | undefined,
): string | undefined {
  if (!shopDomain || !apiKey) return undefined;
  const params = new URLSearchParams({
    template: "index",
    addAppBlockId: `${apiKey}/${THEME_BLOCK_HANDLE}`,
    target: "newAppsSection",
  });
  return `https://${shopDomain}/admin/themes/current/editor?${params.toString()}`;
}

export default function App() {
  const app = useMemo(() => getShopifyGlobal(), []);
  const api = useMemo(() => createApiClient({ app }), [app]);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [workspaceNameDraft, setWorkspaceNameDraft] = useState("");
  const [workspaceNameSaving, setWorkspaceNameSaving] = useState(false);
  const [books, setBooks] = useState<BookListResponse | null>(null);
  const [booksLoading, setBooksLoading] = useState(false);
  // Distinct from booksLoading: this stays true across manual refreshes so that
  // ProductBrowser, once mounted, is never unmounted by a refresh. It gates the
  // first paint of the books card and of ProductBrowser to avoid the large CLS
  // that came from ProductBrowser mounting, then being pushed down when the
  // "Your books" card expanded with loaded rows.
  const [booksInitialized, setBooksInitialized] = useState(false);
  const [copied, setCopied] = useState<{
    bookId: string;
    kind: CopyKind;
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [catalogRenderingMessage, setCatalogRenderingMessage] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const allBookItems = useMemo(
    () => books?.data?.books ?? books?.data?.data ?? [],
    [books],
  );
  // Draft/processing books can't be viewed or embedded, so don't list them.
  const bookItems = useMemo(
    () => allBookItems.filter(isBookReady),
    [allBookItems],
  );
  const hiddenBookCount = allBookItems.length - bookItems.length;

  const shopDomain = connectionStatus?.shopDomain ?? app?.config?.shop;
  const embedBaseUrl = connectionStatus?.embedBaseUrl;
  const workspaceAccessUnavailable = Boolean(
    connectionStatus?.connected && connectionStatus.workspacePlan === "free",
  );
  const canRenameWorkspace = Boolean(
    connectionStatus?.connected && connectionStatus.canRenameWorkspace,
  );
  const workspaceNameRequired =
    canRenameWorkspace && !workspaceAccessUnavailable;
  const workspaceReady = Boolean(
    connectionStatus?.connected &&
    !workspaceAccessUnavailable &&
    !canRenameWorkspace,
  );
  const themeEditorUrl = useMemo(
    () => getThemeEditorUrl(shopDomain, app?.config?.apiKey),
    [shopDomain, app],
  );
  const flipbookerSubscriptionUrl = connectionStatus?.flipbookerAppUrl
    ? `${connectionStatus.flipbookerAppUrl.replace(/\/+$/, "")}/#/choose-plan`
    : undefined;

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (canRenameWorkspace) {
      setWorkspaceNameDraft(connectionStatus?.workspaceName ?? "");
    }
  }, [canRenameWorkspace, connectionStatus?.workspaceName]);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    setError(null);
    try {
      const status = await api.getConnectionStatus();
      setConnectionStatus(status);
      return status;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load status");
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!workspaceAccessUnavailable && !workspaceNameRequired) return;

    const refreshWorkspaceAfterReturn = () => void refreshStatus();
    window.addEventListener("focus", refreshWorkspaceAfterReturn);
    return () =>
      window.removeEventListener("focus", refreshWorkspaceAfterReturn);
  }, [workspaceAccessUnavailable, workspaceNameRequired, refreshStatus]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const { authorizeUrl } = await api.getFlipbookerOAuthUrl();
      const popup = window.open(
        authorizeUrl,
        "flipbooker_oauth",
        "popup,width=720,height=760",
      );

      if (!popup) {
        window.location.href = authorizeUrl;
        return;
      }

      const completed = await new Promise<boolean>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(
          () => {
            if (!settled) {
              settled = true;
              window.removeEventListener("message", handleMessage);
              resolve(false);
            }
          },
          5 * 60 * 1000,
        );

        const intervalId = window.setInterval(() => {
          if (popup.closed && !settled) {
            settled = true;
            window.clearTimeout(timeoutId);
            window.clearInterval(intervalId);
            window.removeEventListener("message", handleMessage);
            resolve(false);
          }
        }, 500);

        function handleMessage(event: MessageEvent) {
          if (
            event.data?.source === "flipbooker-shopify" &&
            event.data?.type === "flipbooker_oauth_complete" &&
            !settled
          ) {
            settled = true;
            window.clearTimeout(timeoutId);
            window.clearInterval(intervalId);
            window.removeEventListener("message", handleMessage);
            resolve(true);
          }
        }

        window.addEventListener("message", handleMessage);
      });

      await refreshStatus();
      if (!completed) {
        setError("Flipbooker connection was not completed.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to connect workspace",
      );
    } finally {
      setConnecting(false);
    }
  }, [api, refreshStatus]);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await api.disconnectFlipbooker();
      setBooks(null);
      await refreshStatus();
      setToastMessage("Flipbooker account disconnected");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  }, [api, refreshStatus]);

  const handleRenameWorkspace = useCallback(async () => {
    const name = workspaceNameDraft.trim();
    if (!name) return;

    setWorkspaceNameSaving(true);
    setError(null);
    try {
      const updated = await api.renameWorkspace(name);
      setConnectionStatus((current) =>
        current
          ? {
              ...current,
              workspaceName: updated.workspaceName,
              canRenameWorkspace: false,
            }
          : current,
      );
      setToastMessage("Workspace name saved");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update workspace name";
      // The name may have been chosen on the platform, which spends the one
      // allowance. Re-read it so the form stops offering what is refused, then
      // report the refusal: refreshing clears the error banner.
      await refreshStatus();
      setError(message);
    } finally {
      setWorkspaceNameSaving(false);
    }
  }, [api, refreshStatus, workspaceNameDraft]);

  const loadBooks = useCallback(async () => {
    setBooksLoading(true);
    setError(null);
    try {
      const bookResponse = await api.listBooks();
      setBooks(bookResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load books");
    } finally {
      setBooksLoading(false);
      // Settle initialization on both success and failure so ProductBrowser
      // renders and the merchant can act (or see the error) either way.
      setBooksInitialized(true);
    }
  }, [api]);

  useEffect(() => {
    if (workspaceReady) {
      void loadBooks();
    } else if (connectionStatus) {
      // Clear books while disconnected or blocked by onboarding so a later
      // ready state performs a fresh initial load with no stale rows.
      setBooks(null);
      setBooksInitialized(false);
    }
  }, [connectionStatus, loadBooks, workspaceReady]);

  const handleCopy = useCallback(
    (book: Book, kind: CopyKind) => {
      let text: string;
      let message: string;
      if (kind === "id") {
        text = getPublicBookId(book);
        message = "Book ID copied to clipboard";
      } else if (kind === "url") {
        text = getEmbedUrl(book, embedBaseUrl);
        message = "Embed URL copied to clipboard";
      } else {
        text = getEmbedCode(book, embedBaseUrl);
        message = "Embed code copied to clipboard";
      }
      if (!text) {
        setError(`No embed URL is available for "${book.name}" yet.`);
        return;
      }
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      void navigator.clipboard.writeText(text);
      setCopied({ bookId: getPublicBookId(book), kind });
      setToastMessage(message);
      copyTimeoutRef.current = setTimeout(() => setCopied(null), 2000);
    },
    [embedBaseUrl],
  );

  const handleCreateCatalog = useCallback(
    async (payload: CatalogCreatePayload) => {
      const { batchId } = await api.createCatalog(payload);

      // Page images render in a background batch. Without a batch id we can only
      // guess when it's ready, so fall back to a delayed refresh.
      if (!batchId) {
        setToastMessage(
          "Catalog created — it will appear in Your books shortly.",
        );
        window.setTimeout(() => void loadBooks(), 5000);
        return;
      }

      setToastMessage(null);
      setCatalogRenderingMessage(
        "Catalog is rendering — this can take a moment…",
      );

      const terminal = new Set([
        "complete",
        "partial_failure",
        "failed",
        "cancelled",
      ]);
      const longRunningThreshold = Date.now() + 120_000;
      let longRunningNoticeShown = false;

      const poll = async () => {
        try {
          const status = await api.getBatchStatus(batchId);
          if (terminal.has(status.status)) {
            setCatalogRenderingMessage(null);
            if (status.status === "complete") {
              setToastMessage("Catalog is ready — find it in Your books.");
            } else if (status.status === "partial_failure") {
              setToastMessage(
                "Catalog created, but some pages failed to render.",
              );
            } else if (status.status === "cancelled") {
              setToastMessage("Catalog rendering was cancelled.");
            } else {
              setToastMessage("Catalog rendering failed. Please try again.");
            }
            void loadBooks();
            return;
          }
        } catch {
          // Transient error (token refresh, network) — keep polling until the
          // batch reports a terminal state.
        }

        if (!longRunningNoticeShown && Date.now() >= longRunningThreshold) {
          longRunningNoticeShown = true;
          setCatalogRenderingMessage(
            "Catalog is still rendering — it will appear in Your books once ready.",
          );
        }

        window.setTimeout(
          () => void poll(),
          longRunningNoticeShown ? 10_000 : 3000,
        );
      };

      window.setTimeout(() => void poll(), 3000);
    },
    [api, loadBooks],
  );

  const navigation = (
    <Navigation location="/">
      <Navigation.Section
        items={[{ label: "Dashboard", url: "/", selected: true }]}
      />
    </Navigation>
  );

  return (
    <Frame navigation={navigation}>
      <Page
        title="Flipbooker for Shopify"
        subtitle="Embed interactive catalogs into your storefront"
      >
        <Layout>
          <Layout.Section>
            <Card>
              <Card.Header title="Connected workspace" />
              <Card.Section>
                {statusLoading ? (
                  <Spinner accessibilityLabel="Loading status" />
                ) : (
                  <InlineStack gap="200" align="space-between">
                    <BlockStack gap="100">
                      <Text variant="headingMd" as="h2">
                        {connectionStatus?.connected
                          ? connectionStatus.workspaceName ||
                            "Workspace connected"
                          : "Not connected"}
                      </Text>
                    </BlockStack>
                    {connectionStatus?.connected ? (
                      <Button
                        tone="critical"
                        onClick={handleDisconnect}
                        loading={disconnecting}
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <div style={{ maxWidth: 320 }}>
                        <Text variant="bodyMd" as="p" tone="subdued">
                          Connect an existing Flipbooker account to load books
                          and create Shopify embeds.
                        </Text>
                        <div style={{ marginTop: "1rem" }}>
                          <Button
                            variant="primary"
                            onClick={handleConnect}
                            loading={connecting}
                            fullWidth
                          >
                            Connect with Flipbooker
                          </Button>
                        </div>
                      </div>
                    )}
                  </InlineStack>
                )}
                {error && (
                  <Text tone="critical" variant="bodyMd" as="p">
                    {error}
                  </Text>
                )}
              </Card.Section>
            </Card>
          </Layout.Section>

          {workspaceNameRequired && (
            <Layout.Section>
              <Card>
                <Card.Header title="Choose your workspace name" />
                <Card.Section>
                  <BlockStack gap="300">
                    <Text as="p" tone="subdued">
                      Choose your business or team name before creating or
                      publishing books. You can do this only once.
                    </Text>
                    <TextField
                      label="Workspace name"
                      value={workspaceNameDraft}
                      onChange={setWorkspaceNameDraft}
                      autoComplete="off"
                      maxLength={255}
                    />
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={handleRenameWorkspace}
                        loading={workspaceNameSaving}
                        disabled={
                          !workspaceNameDraft.trim() ||
                          workspaceNameDraft.trim() ===
                            connectionStatus?.workspaceName
                        }
                      >
                        Save workspace name
                      </Button>
                      <Button onClick={() => void refreshStatus()}>
                        Refresh workspace
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Card.Section>
              </Card>
            </Layout.Section>
          )}

          {workspaceAccessUnavailable && (
            <Layout.Section>
              <Banner tone="warning">
                <BlockStack gap="200">
                  <Text as="p">
                    The Shopify app is free for Flipbooker subscribers. Your
                    connected workspace does not have an active subscription on
                    the Flipbooker platform. Complete your subscription there,
                    then return to this app to continue.
                  </Text>
                  {flipbookerSubscriptionUrl && (
                    <InlineStack>
                      <Button
                        variant="primary"
                        url={flipbookerSubscriptionUrl}
                        external
                      >
                        Continue on Flipbooker
                      </Button>
                    </InlineStack>
                  )}
                </BlockStack>
              </Banner>
            </Layout.Section>
          )}

          {workspaceReady && (
            <Layout.Section>
              <Banner
                tone="info"
                action={
                  themeEditorUrl
                    ? {
                        content: "Open theme editor",
                        url: themeEditorUrl,
                        external: true,
                      }
                    : undefined
                }
              >
                Copy a book&rsquo;s <strong>Book ID</strong> below, then add the{" "}
                <strong>Flipbooker Embed</strong> block to a page and paste it
                into <strong>Book to show</strong>. The book appears
                automatically — no publishing.
              </Banner>
            </Layout.Section>
          )}

          {workspaceReady && (
            <Layout.Section>
              <Card>
                <Card.Header
                  title="Your books"
                  actions={[
                    {
                      content: "Refresh",
                      onAction: () => void loadBooks(),
                      disabled: booksLoading,
                    },
                  ]}
                />
                {!booksInitialized && (
                  <Card.Section>
                    <Spinner accessibilityLabel="Loading books" />
                  </Card.Section>
                )}
                {booksInitialized && bookItems.length === 0 && (
                  <Card.Section>
                    <Text tone="subdued" as="p">
                      {hiddenBookCount > 0
                        ? "No ready books yet. Finish your draft books in Flipbooker and they will appear here."
                        : "No books yet. Create a catalog in Flipbooker and it will appear here."}
                    </Text>
                  </Card.Section>
                )}
                {booksInitialized &&
                  bookItems.length > 0 &&
                  hiddenBookCount > 0 && (
                    <Card.Section subdued>
                      <Text tone="subdued" variant="bodySm" as="p">
                        {hiddenBookCount} unfinished{" "}
                        {hiddenBookCount === 1 ? "book is" : "books are"} hidden
                        — finish them in Flipbooker to use them here.
                      </Text>
                    </Card.Section>
                  )}
                {booksInitialized &&
                  bookItems.map((book) => {
                    const publicBookId = getPublicBookId(book);
                    return (
                      <Card.Section key={publicBookId || book.preview_url}>
                        <BlockStack gap="300">
                          <BlockStack gap="100">
                            <Text variant="headingSm" as="h3">
                              {book.name}
                            </Text>
                            <Text variant="bodySm" tone="subdued" as="p">
                              Book ID: {publicBookId || "Unavailable"}
                              {book.page_count
                                ? ` • ${book.page_count} pages`
                                : ""}
                              {book.created_at
                                ? ` • Created ${new Date(book.created_at).toLocaleDateString()}`
                                : ""}
                            </Text>
                          </BlockStack>
                          <InlineStack gap="200">
                            {book.preview_url && (
                              <Button
                                onClick={() =>
                                  window.open(
                                    book.preview_url,
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                              >
                                View book
                              </Button>
                            )}
                            <Button
                              variant="primary"
                              onClick={() => handleCopy(book, "id")}
                              disabled={!publicBookId}
                            >
                              {copied?.bookId === publicBookId &&
                              copied.kind === "id"
                                ? "Copied!"
                                : "Copy Book ID"}
                            </Button>
                            <Button onClick={() => handleCopy(book, "url")}>
                              {copied?.bookId === publicBookId &&
                              copied.kind === "url"
                                ? "Copied!"
                                : "Copy URL"}
                            </Button>
                            <Button onClick={() => handleCopy(book, "code")}>
                              {copied?.bookId === publicBookId &&
                              copied.kind === "code"
                                ? "Copied!"
                                : "Copy embed code"}
                            </Button>
                          </InlineStack>
                        </BlockStack>
                      </Card.Section>
                    );
                  })}
              </Card>
            </Layout.Section>
          )}

          {workspaceReady && booksInitialized && (
            <Layout.Section>
              <Box paddingBlockEnd="600">
                <ProductBrowser
                  api={api}
                  onCreateCatalog={handleCreateCatalog}
                  onNotice={setToastMessage}
                />
              </Box>
            </Layout.Section>
          )}
        </Layout>
      </Page>
      {(catalogRenderingMessage || toastMessage) && (
        <Toast
          content={catalogRenderingMessage ?? toastMessage ?? ""}
          duration={
            catalogRenderingMessage ? PERSISTENT_TOAST_DURATION : undefined
          }
          onDismiss={() => {
            if (catalogRenderingMessage) {
              setCatalogRenderingMessage(null);
            } else {
              setToastMessage(null);
            }
          }}
        />
      )}
    </Frame>
  );
}
